import { DoseInputError } from '~/domain/dose/doseFactor';
import { ApprovableRegistration } from '~/domain/patient/approvableRegistration';
import { isDeformableRegistration } from '~/domain/patient/deformableRegistration';
import { isRigidRegistration } from '~/domain/patient/rigidRegistration';
import {
    rigidVolumeMapping,
    VolumeMapping,
} from '~/domain/services/resampleVolume';

export type ReviewTransformKind = 'deformable' | 'rigid';

export interface ReviewTransformOption {
    readonly sopInstanceUid: string;
    readonly kind: ReviewTransformKind;
    readonly registration: ApprovableRegistration;
}

// The run's deformable and one rigid: the rigid the run returned, preferred over the initial rigid it was given back.
export function reviewTransformOptions(
    runUids: readonly string[],
    initRigidUid: string | undefined,
    resolve: (uid: string) => ApprovableRegistration | undefined
): ReviewTransformOption[] {
    const held = runUids.flatMap((uid) => {
        const registration = resolve(uid);
        return registration ? [{ uid, registration }] : [];
    });
    const deformable = held.find(({ registration }) =>
        isDeformableRegistration(registration)
    );
    const rigids = held.filter(({ registration }) =>
        isRigidRegistration(registration)
    );
    const rigid =
        rigids.find(({ uid }) => uid !== initRigidUid) ?? rigids.at(0);
    return [
        ...(deformable
            ? [
                  {
                      sopInstanceUid: deformable.uid,
                      kind: 'deformable' as const,
                      registration: deformable.registration,
                  },
              ]
            : []),
        ...(rigid
            ? [
                  {
                      sopInstanceUid: rigid.uid,
                      kind: 'rigid' as const,
                      registration: rigid.registration,
                  },
              ]
            : []),
    ];
}

const mismatch = () =>
    new DoseInputError(
        'The registration does not relate the target scan to the planning scan.',
        'replanning.review.transform.mismatch'
    );

// Target point to planning point through the rigid, refusing a registration between any other pair of frames.
export function rigidReviewMapping(
    registration: ApprovableRegistration,
    targetFrameOfReferenceUid: string,
    planningFrameOfReferenceUid: string
): VolumeMapping {
    if (
        !isRigidRegistration(registration) ||
        targetFrameOfReferenceUid === planningFrameOfReferenceUid
    ) {
        throw mismatch();
    }
    const targetToPlanning = registration.getTransformFromTo(
        targetFrameOfReferenceUid,
        planningFrameOfReferenceUid
    );
    if (!targetToPlanning) {
        throw mismatch();
    }
    return rigidVolumeMapping(targetToPlanning);
}
